// The Hub page: sign in, route on the address hash, render a page, and handle
// the page's forms and buttons. Pages are plain functions that return HTML,
// so all the logic worth testing lives in them and in the modules they use.

/// <reference types="vite/client" />

import './styles.css';
import content from './content/sample-content.json';
import { agentScope, mountAgent, readAgentConfig } from './agent';
import { agentFallbackHtml, mountAgentDock, opensAgent, type AgentDock } from './agent-dock';
import { ApiError, createApi, esc } from './api';
import {
  accountRoles,
  apiToken,
  autoSignIn,
  autoSignInDecisionForThisTab,
  readConfig,
  signIn,
  SignInNeededError,
  signOut,
  startSignIn,
  tokenFor,
} from './auth';
import { buildPreview, type ChangeEvent, type Counts, type Preview } from './notifications';
import { buildIndex, bySystem, routeFor, search } from './search';
import { readSubscriptionForm, renderCalendar, renderEntry, type CalendarEntry, type OwnSubscription } from './pages/calendar';
import { renderGuide, renderGuideList, type Guide } from './pages/guides';
import { renderHome, renderSystem } from './pages/home';
import { readEntryForm, readGuideForm, renderPublish, type HistoryRow, type HistoryView, type RecordKind } from './pages/publish';
import { renderTeleos } from './pages/teleos';
import { renderTools } from './pages/tools';
import { renderLayout, renderSignedOut } from './shell';

const TECHNOLOGY_ROLE = 'Hub.Technology';
const root = document.getElementById('app') as HTMLElement;
const api = createApi(apiToken);

const state: {
  name: string;
  isTechnology: boolean;
  publishPreview: Preview | null;
  publishErrors: string[];
  history: HistoryView | null;
  calendarStatus: string;
  signInMessage: string;
} = { name: '', isTechnology: false, publishPreview: null, publishErrors: [], history: null, calendarStatus: '', signInMessage: '' };

function layout(body: string, page: string, query: string): string {
  return renderLayout(
    { name: state.name, isTechnology: state.isTechnology, notice: content.notice, page, query, tools: content.tools },
    body,
  );
}

// Shown instead of the page when a token cannot be had without signing in.
function signInNotice(message: string): string {
  return `<div class="notice" role="alert"><p>${esc(message)}</p><button type="button" data-action="sign-in">Sign in again</button></div>`;
}

const formValues = (form: HTMLFormElement) => {
  const values: Record<string, string> = {};
  new FormData(form).forEach((v, k) => {
    values[k] = String(v);
  });
  return values;
};

async function entries(): Promise<CalendarEntry[]> {
  return (await api.get<{ entries: CalendarEntry[] }>('/api/calendar')).entries;
}

async function historyOf(kind: RecordKind, id: string): Promise<HistoryRow[]> {
  return (await api.get<{ history: HistoryRow[] }>(`/api/history/${kind}/${encodeURIComponent(id)}`)).history;
}

async function previewFor(event: ChangeEvent, entry: CalendarEntry): Promise<Preview> {
  const { counts } = await api.get<{ counts: Counts }>(`/api/subscriptions/counts?systems=${encodeURIComponent(entry.systems.join(','))}`);
  return buildPreview(event, entry, counts);
}

async function render(): Promise<void> {
  const { page, id } = routeFor(location.hash);
  let body = '';
  try {
    if (page === 'home') {
      const list = await entries();
      body = renderHome(content, list.filter((e) => Date.parse(e.scheduledStart) >= Date.now() - 86_400_000));
    } else if (page === 'search') {
      const list = await entries();
      body = renderHome(content, list, id, search(buildIndex(content, list), id));
    } else if (page === 'system') {
      body = renderSystem(id, bySystem(buildIndex(content, await entries()), id));
    } else if (page === 'entry') {
      body = renderEntry((await api.get<{ entry: CalendarEntry }>(`/api/calendar/${encodeURIComponent(id)}`)).entry);
    } else if (page === 'calendar') {
      const filter = new URLSearchParams(location.search).get('system') ?? '';
      const [list, own] = await Promise.all([entries(), api.get<{ subscription: OwnSubscription | null }>('/api/subscriptions/me')]);
      body = renderCalendar(list, content.systems, filter, own.subscription, state.calendarStatus);
    } else if (page === 'teleos') {
      body = renderTeleos(content, await entries());
    } else if (page === 'tools') {
      body = renderTools(content.tools);
    } else if (page === 'guides') {
      const published = (await api.get<{ guides: Guide[] }>('/api/guides')).guides;
      const all = [...(content.guides as Guide[]), ...published];
      const guide = id ? all.find((g) => g.id === id) : undefined;
      body = guide ? renderGuide(guide) : renderGuideList(all, content.training);
    } else if (page === 'publish' && state.isTechnology) {
      const [list, { guides }] = await Promise.all([entries(), api.get<{ guides: Guide[] }>('/api/guides')]);
      body = renderPublish(list, guides, state.publishPreview, state.publishErrors, state.history);
    } else {
      body = '<h1>Not found</h1><p><a href="#/">Back to the Hub</a></p>';
    }
  } catch (err) {
    body =
      err instanceof SignInNeededError
        ? signInNotice(err.message)
        : `<div class="notice" role="alert">${esc(err instanceof ApiError ? err.message : 'Something went wrong loading this page.')}</div>`;
    if (err instanceof SignInNeededError) state.signInMessage = '';
  }
  // A button or form that needed a token it could not get says so above the page.
  if (state.signInMessage) {
    body = signInNotice(state.signInMessage) + body;
    state.signInMessage = '';
  }
  root.innerHTML = layout(body, page, page === 'search' ? id : '');
  // A link to one tool opens the directory at that tool.
  if (page === 'tools' && id) document.getElementById(id)?.scrollIntoView();
}

// Para Agent's chat bubble, on every signed-in page.
let agentDock: AgentDock | null = null;

function startAgentDock(): AgentDock {
  const config = readAgentConfig(import.meta.env as Record<string, string | undefined>);
  return mountAgentDock(document.body, (element, signal) =>
    mountAgent(element, {
      config,
      fetchToken: () => tokenFor(agentScope),
      userName: state.name,
      fallbackHtml: agentFallbackHtml(content.support),
      signal,
    }),
  );
}

async function onSubmit(form: HTMLFormElement): Promise<void> {
  const action = form.dataset.action;
  const id = form.dataset.id ?? '';
  const values = formValues(form);
  state.publishErrors = [];
  try {
    switch (action) {
      case 'search':
        location.hash = `#/search/${encodeURIComponent(values.q ?? '')}`;
        return;
      case 'filter-calendar': {
        const url = new URL(location.href);
        if (values.system) url.searchParams.set('system', values.system);
        else url.searchParams.delete('system');
        history.replaceState(null, '', url);
        break;
      }
      case 'save-subscription': {
        const systems = new FormData(form).getAll('systems').map(String);
        await api.put('/api/subscriptions/me', readSubscriptionForm({ scope: values.scope ?? 'some', systems }));
        state.calendarStatus = 'Saved.';
        break;
      }
      case 'create-entry': {
        const { entry } = await api.post<{ entry: CalendarEntry }>('/api/calendar', { ...readEntryForm(values), publish: values.publish === 'on' });
        state.publishPreview = entry.published ? await previewFor('created', entry) : null;
        break;
      }
      case 'reschedule-entry': {
        const { entry } = await api.post<{ entry: CalendarEntry }>(`/api/calendar/${encodeURIComponent(id)}/reschedule`, { scheduledStart: values.scheduledStart });
        state.publishPreview = await previewFor('rescheduled', entry);
        break;
      }
      case 'cancel-entry': {
        const { entry } = await api.post<{ entry: CalendarEntry }>(`/api/calendar/${encodeURIComponent(id)}/cancel`, { reason: values.reason });
        state.publishPreview = await previewFor('cancelled', entry);
        break;
      }
      case 'publish-guide':
        await api.post('/api/guides', readGuideForm(values));
        break;
      case 'edit-guide':
        await api.put(`/api/guides/${encodeURIComponent(id)}`, readGuideForm(values));
        if (state.history?.kind === 'guide' && state.history.id === id) state.history = { kind: 'guide', id, rows: await historyOf('guide', id) };
        break;
      default:
        return;
    }
  } catch (err) {
    if (err instanceof ApiError) {
      state.publishErrors = err.errors.length ? err.errors : [err.message];
      state.calendarStatus = err.message;
    } else if (err instanceof SignInNeededError) {
      state.signInMessage = err.message;
    } else throw err;
  }
  await render();
}

async function onClick(button: HTMLElement): Promise<void> {
  const action = button.dataset.action;
  const id = button.dataset.id ?? '';
  const kind: RecordKind = button.dataset.kind === 'guide' ? 'guide' : 'entry';
  try {
    switch (action) {
      case 'sign-out':
        await signOut();
        return;
      case 'sign-in':
        await signIn();
        return;
      case 'publish-entry': {
        const { entry } = await api.post<{ entry: CalendarEntry }>(`/api/calendar/${encodeURIComponent(id)}/publish`);
        state.publishPreview = await previewFor('created', entry);
        break;
      }
      case 'show-history':
        state.history = { kind, id, rows: await historyOf(kind, id) };
        break;
      case 'rollback':
        await api.post(`/api/history/${kind}/${encodeURIComponent(id)}/rollback`, { version: Number(button.dataset.version) });
        state.history = { kind, id, rows: await historyOf(kind, id) };
        break;
      default:
        return;
    }
  } catch (err) {
    if (err instanceof ApiError) {
      state.publishErrors = err.errors.length ? err.errors : [err.message];
    } else if (err instanceof SignInNeededError) {
      state.signInMessage = err.message;
    } else throw err;
  }
  await render();
}

const COULD_NOT_SIGN_IN = 'We could not sign you in. Sign in to continue.';

// The landing screen, with its button wired. A sign-in that fails before
// leaving the page says so and keeps the button working.
function showSignedOut(message: string): void {
  root.innerHTML = renderSignedOut(content.notice, message);
  document.getElementById('sign-in')?.addEventListener('click', () => {
    signIn().catch(() => showSignedOut(COULD_NOT_SIGN_IN));
  });
}

async function boot(): Promise<void> {
  const config = readConfig(import.meta.env as Record<string, string | undefined>);
  if (!config) {
    root.innerHTML = '<div class="notice" role="alert">This Hub build is missing its sign-in settings. See RUNBOOK.md.</div>';
    return;
  }
  let account: Awaited<ReturnType<typeof startSignIn>>;
  try {
    account = await startSignIn(config);
  } catch {
    // Microsoft sent back an error. Offer the button, never a blank page.
    showSignedOut(COULD_NOT_SIGN_IN);
    return;
  }
  if (!account) {
    // Sign in automatically once per tab; after Sign out, or once that has
    // been tried, show the button.
    if (autoSignInDecisionForThisTab() === 'skip') {
      showSignedOut('');
      return;
    }
    showSignedOut('Signing you in with your Paramount Liquor account…');
    const result = await autoSignIn();
    showSignedOut(result === 'failed' ? COULD_NOT_SIGN_IN : '');
    return;
  }
  state.name = account.name ?? account.username;
  state.isTechnology = accountRoles(account).includes(TECHNOLOGY_ROLE);

  root.addEventListener('submit', (e) => {
    const form = e.target as HTMLFormElement;
    if (!form.dataset.action) return;
    e.preventDefault();
    void onSubmit(form);
  });
  // There is no Support page. A link to it, or its address, opens the chat
  // and leaves the person on the page they were on.
  agentDock = startAgentDock();
  if (opensAgent(location.hash)) {
    history.replaceState(null, '', '#/');
    agentDock.open();
  }

  root.addEventListener('click', (e) => {
    // The skip link moves focus to the page content. It must not change the
    // address: the Hub routes on the hash, and #main is not a page.
    if ((e.target as HTMLElement).closest('a[data-skip]')) {
      e.preventDefault();
      document.getElementById('main')?.focus();
      return;
    }
    const link = (e.target as HTMLElement).closest<HTMLElement>('a[href^="#"]');
    if (link && opensAgent(link.getAttribute('href'))) {
      e.preventDefault();
      agentDock?.open(link);
      return;
    }
    // A link to the page already open opens that page again instead of doing
    // nothing.
    if (link && link.getAttribute('href') === location.hash) {
      e.preventDefault();
      void render();
      return;
    }
    const button = (e.target as HTMLElement).closest('button[data-action]') as HTMLElement | null;
    if (button && button.getAttribute('type') === 'button') void onClick(button);
  });
  window.addEventListener('hashchange', (e) => {
    // A typed or bookmarked #/support: put the previous address back.
    if (opensAgent(location.hash)) {
      const previous = new URL(e.oldURL).hash;
      history.replaceState(null, '', opensAgent(previous) || !previous ? '#/' : previous);
      agentDock?.open();
      return;
    }
    state.publishPreview = null;
    state.history = null;
    state.calendarStatus = '';
    void render();
  });
  await render();
}

void boot();
